import { getLoginPath } from '@/lib/paths';
import { AUTH_STORAGE_KEYS, clearAllAuthStorage } from '@/constants/auth';
import type {
  MasterUserListResponse,
  MasterUserListFilters,
  MasterUserDetail,
  MasterUser,
  MasterUserCreateRequest,
  MasterUserUpdateRequest,
  MasterUserResetPasswordRequest,
  MasterUserDisableTwoFactorRequest,
  MasterUserRevokeSessionsRequest,
  MasterUserDeleteRequest,
  MasterRole,
  MasterRoleDetail,
  MasterRoleCreateRequest,
  MasterRoleUpdateRequest,
  MasterPermissionCatalog,
  MasterRolePermissionAssignmentRequest,
  MasterRolePermissionAssignmentResponse,
  MasterRefreshTokenInfo,
  CrossTenantUserListResponse,
  CrossTenantTenantOption,
} from '@easyflow/shared';
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

/**
 * Errore HTTP con body strutturato: consente ai consumer di leggere
 * `code`/`data` della risposta (es. 409 STORE_NOT_EMPTY con references).
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }
}

/** Opzioni di richiesta del client master (fetch + estensioni locali). */
export interface MasterRequestInit extends RequestInit {
  /** Timeout della singola richiesta (default 30 s). */
  timeoutMs?: number;
  /** Interno: la richiesta è già il replay post-refresh (mai un secondo giro). */
  retriedAfterRefresh?: boolean;
}

/** Endpoint di autenticazione: un 401 qui è una risposta di dominio, non un token scaduto. */
const AUTH_ENDPOINT_PREFIXES = ['/auth/', '/master/login', '/master/refresh-token', '/master/logout', '/bff/'] as const;

function isAuthEndpoint(endpoint: string): boolean {
  return AUTH_ENDPOINT_PREFIXES.some((prefix) => endpoint.startsWith(prefix));
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      if (storedToken) {
        this.token = storedToken;
      }
    }
  }

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  // Generic HTTP methods
  async get<T = unknown>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = unknown>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'POST',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    });
  }

  async put<T = unknown>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    });
  }

  async patch<T = unknown>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    });
  }

  async delete<T = unknown>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async request<T>(
    endpoint: string,
    options: MasterRequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: HeadersInit = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>).Authorization = `Bearer ${this.token}`;
    }

    try {
      const controller = new AbortController();
      const { timeoutMs: requestedTimeoutMs, retriedAfterRefresh, ...fetchOptions } = options;
      const timeoutMs = requestedTimeoutMs || 30000;
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include',
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeoutId);

      // Task #1527 — propaga header X-Storage-Quota-* anche dal master FE,
      // così l'eventuale widget tenant che il master apre via impersonation
      // riceve gli aggiornamenti passivi. Allineato a packages/frontend.
      try {
        const r2Pct = response.headers.get('X-Storage-Quota-R2-Pct');
        const r2St  = response.headers.get('X-Storage-Quota-R2-Status');
        const dbPct = response.headers.get('X-Storage-Quota-DB-Pct');
        const dbSt  = response.headers.get('X-Storage-Quota-DB-Status');
        const blocked = response.headers.get('X-Storage-Quota-Blocked-Categories');
        const emergency = response.headers.get('X-Storage-Quota-Emergency-Until');
        if (r2Pct || dbPct || r2St || dbSt || blocked || emergency) {
          window.dispatchEvent(new CustomEvent('edgvoip:storage-quota-header', {
            detail: {
              r2Pct: r2Pct !== null ? Number.parseFloat(r2Pct) : null,
              r2Status: r2St,
              dbPct: dbPct !== null ? Number.parseFloat(dbPct) : null,
              dbStatus: dbSt,
              blockedCategories: blocked ? blocked.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
              emergencyUntil: emergency,
              ts: Date.now(),
            },
          }));
        }
      } catch { /* SSR/old browser safe */ }

      const data = await response.json();

      if (!response.ok) {
        // Task #1527 — toast bloccante per quote sature.
        if (response.status === 422 && data && typeof data === 'object') {
          const code = (data as { error_code?: unknown; code?: unknown }).error_code
            ?? (data as { code?: unknown }).code;
          if (code === 'STORAGE_QUOTA_EXCEEDED' || code === 'DB_QUOTA_EXCEEDED_EMBEDDINGS') {
            try {
              window.dispatchEvent(new CustomEvent('edgvoip:storage-quota-error', {
                detail: {
                  errorCode: code,
                  message: typeof (data as { error?: unknown }).error === 'string'
                    ? (data as { error: string }).error
                    : `Quota raggiunta (${String(code)})`,
                  scope: code === 'DB_QUOTA_EXCEEDED_EMBEDDINGS' ? 'db' : 'r2',
                  blockedCategory: code === 'DB_QUOTA_EXCEEDED_EMBEDDINGS' ? 'embedding' : 'upload',
                  pct: typeof (data as { pct?: unknown }).pct === 'number'
                    ? (data as { pct: number }).pct : null,
                  requestId: response.headers.get('X-Request-Id'),
                },
              }));
            } catch { /* SSR safe */ }
          }
        }
        // 401: access token appena scaduto? Rinnovo JWT (single-flight, via
        // BFF così anche il cookie viene ri-impostato) e replay UNA volta.
        // Login solo se il refresh fallisce davvero.
        if (response.status === 401) {
          if (
            !retriedAfterRefresh
            && !isAuthEndpoint(endpoint)
            && localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
          ) {
            const { refreshToken } = await import('./tokenRefresh');
            const outcome = await refreshToken();
            if (outcome.status === 'refreshed') {
              return this.request<T>(endpoint, { ...options, retriedAfterRefresh: true });
            }
            if (outcome.status === 'unavailable') {
              // Rinnovo transitoriamente impossibile (rete/5xx): la sessione
              // resta intatta, l'errore risale al chiamante e si riproverà.
              throw new ApiRequestError('Rinnovo sessione non disponibile, riprova', response.status, data);
            }
          }
          // Fallback finale: sessione BFF ancora valida? Altrimenti login.
          try {
            // Task #3585 — X-Auth-Scope esplicito: sul sottodominio dedicato la
            // SPA vive alla root e il referer non contiene più '/master', quindi
            // lo scope master va dichiarato (non dedotto dal path).
            const sessionResponse = await fetch('/api/bff/session', {
              method: 'GET',
              credentials: 'include',
              headers: { 'X-Auth-Scope': 'master' },
            });
            
            if (!sessionResponse.ok) {
              this.clearToken();
              clearAllAuthStorage();
              window.location.href = getLoginPath();
              return data;
            }
          } catch (sessionError) {
            this.clearToken();
            clearAllAuthStorage();
            window.location.href = getLoginPath();
            return data;
          }
        }
        const errorMessage = typeof data.error === 'string' 
          ? data.error 
          : data.error?.message || data.error?.code || JSON.stringify(data.error) || `HTTP ${response.status}`;
        throw new ApiRequestError(errorMessage, response.status, data);
      }

      return data;
    } catch (error) {
      // Don't log errors for optional endpoints (alerts, notifications, security, etc.)
      const isOptionalEndpoint = url.includes('/alerts') 
        || url.includes('/notifications')
        || url.includes('/security/'); // Generic security endpoint check
      
      if (!isOptionalEndpoint && error instanceof Error && !error.message.includes('404')) {
        console.error('API request failed:', error);
      }
      throw error;
    }
  }

  // Health check
  async health(): Promise<ApiResponse> {
    return this.request('/health');
  }

  // Tenants
  async getTenants(params?: { page?: number; limit?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/tenants?${query.toString()}`);
  }

  async getTenant(id: string) {
    return this.request(`/tenants/${id}`);
  }

  async createTenant(data: { name: string; domain: string; edg_suite_id: string }) {
    return this.request('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTenant(id: string, data: Partial<{ name: string; domain: string; edg_suite_id: string }>) {
    return this.request(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTenant(id: string) {
    return this.request(`/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  // Stores
  async getStores(params?: { page?: number; limit?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/stores?${query.toString()}`);
  }

  async getStore(id: string) {
    return this.request(`/stores/${id}`);
  }

  async createStore(data: { name: string; store_id: string; sip_trunk_settings?: Record<string, unknown> }) {
    return this.request('/stores', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStore(id: string, data: Partial<{ name: string; store_id: string; sip_trunk_settings?: Record<string, unknown> }>) {
    return this.request(`/stores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteStore(id: string) {
    return this.request(`/stores/${id}`, {
      method: 'DELETE',
    });
  }

  // VoIP Entities - Extensions
  async getExtensions(params?: { page?: number; limit?: number; store_id?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/voip/sip-extensions?${query.toString()}`);
  }

  async createW3Extension(data: { ext_number: string; display_name: string; enabled?: boolean; voicemail_enabled?: boolean; class_of_service?: string; note?: string; store_id?: string }) {
    return this.request('/w3-voip/extensions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createW3Trunk(data: { provider: string; proxy: string; port?: number; transport?: string; auth_username: string; secret_ref: string; register?: boolean; expiry_seconds?: number; codec_set?: string; note?: string; store_id?: string }) {
    return this.request('/w3-voip/trunks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Time Conditions
  async getTimeConditions(params?: { page?: number; limit?: number; store_id?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/voip/time-conditions?${query.toString()}`);
  }

  async createTimeCondition(data: { name: string; description?: string; timezone?: string; store_id?: string }) {
    return this.request('/voip/time-conditions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - IVR Menus
  async getIvrMenus(params?: { page?: number; limit?: number; store_id?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/voip/ivr-menus?${query.toString()}`);
  }

  async createIvrMenu(data: { name: string; description?: string; greeting_message?: string; store_id?: string }) {
    return this.request('/voip/ivr-menus', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Ring Groups
  async getRingGroups(params?: { page?: number; limit?: number; store_id?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/voip/ring-groups?${query.toString()}`);
  }

  async createRingGroup(data: { name: string; extension_number: string; description?: string; store_id?: string }) {
    return this.request('/voip/ring-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Queues
  async getQueues(params?: { page?: number; limit?: number; store_id?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/voip/queues?${query.toString()}`);
  }

  async createQueue(data: { name: string; extension_number: string; description?: string; store_id?: string }) {
    return this.request('/voip/queues', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Conference Rooms
  async getConferenceRooms(params?: { page?: number; limit?: number; store_id?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/voip/conference-rooms?${query.toString()}`);
  }

  async createConferenceRoom(data: { name: string; extension_number: string; description?: string; store_id?: string }) {
    return this.request('/voip/conference-rooms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // VoIP Entities - Voicemail Boxes
  async getVoicemailBoxes(params?: { page?: number; limit?: number; store_id?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.store_id) query.append('store_id', params.store_id);
    if (params?.search) query.append('search', params.search);
    
    return this.request(`/voip/voicemail-boxes?${query.toString()}`);
  }

  async createVoicemailBox(data: { extension_number: string; password: string; display_name: string; email_address?: string; store_id?: string }) {
    return this.request('/voip/voicemail-boxes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ===== W3 VOIP API METHODS =====
  
  // W3 Trunks
  async getW3Trunks(params?: { store_id?: string }) {
    const query = new URLSearchParams();
    if (params?.store_id) query.append('store_id', params.store_id);
    
    return this.request(`/w3-voip/trunks?${query.toString()}`);
  }

  async getW3Trunk(id: string) {
    return this.request(`/w3-voip/trunks/${id}`);
  }

  async updateW3Trunk(id: string, data: Record<string, unknown>) {
    return this.request(`/w3-voip/trunks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteW3Trunk(id: string) {
    return this.request(`/w3-voip/trunks/${id}`, {
      method: 'DELETE',
    });
  }

  // W3 Extensions
  async getW3Extensions(params?: { store_id?: string }) {
    const query = new URLSearchParams();
    if (params?.store_id) query.append('store_id', params.store_id);
    
    return this.request(`/w3-voip/extensions?${query.toString()}`);
  }

  async getW3Extension(id: string) {
    return this.request(`/w3-voip/extensions/${id}`);
  }

  async updateW3Extension(id: string, data: Record<string, unknown>) {
    return this.request(`/w3-voip/extensions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteW3Extension(id: string) {
    return this.request(`/w3-voip/extensions/${id}`, {
      method: 'DELETE',
    });
  }

  // W3 DIDs
  async getW3Dids(params?: { store_id?: string }) {
    const query = new URLSearchParams();
    if (params?.store_id) query.append('store_id', params.store_id);
    
    return this.request(`/w3-voip/dids?${query.toString()}`);
  }

  async createW3Did(data: { trunk_id: string; e164: string; route_target_type: string; route_target_ref: string; label: string; active?: boolean; store_id?: string }) {
    return this.request('/w3-voip/dids', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // W3 Routes
  async getW3Routes() {
    return this.request('/w3-voip/routes');
  }

  async createW3Route(data: { name: string; pattern: string; strip_digits?: number; prepend?: string; trunk_id: string; priority?: number; active?: boolean }) {
    return this.request('/w3-voip/routes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // W3 Contact Policies
  async getW3ContactPolicies() {
    return this.request('/w3-voip/contact-policies');
  }

  async createW3ContactPolicy(data: { scope_type: string; scope_ref: string; rules_json: Record<string, unknown>; label: string; active?: boolean }) {
    return this.request('/w3-voip/contact-policies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // CDR and Activity Logs
  async getCdrs(params?: { store_id?: string; start_date?: string; end_date?: string; direction?: string; disposition?: string; ext_number?: string; did_e164?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });
    
    return this.request(`/cdr?${query.toString()}`);
  }

  async getCdrStats(params?: { store_id?: string; start_date?: string; end_date?: string }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });
    
    return this.request(`/cdr/stats?${query.toString()}`);
  }

  async getActivityLogs(params?: { actor?: string; action?: string; target_type?: string; target_id?: string; status?: string; start_date?: string; end_date?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });
    
    return this.request(`/cdr-activity/activity-log?${query.toString()}`);
  }

  async getActivityStats(params?: { start_date?: string; end_date?: string }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });
    
    return this.request(`/cdr-activity/activity-log/stats?${query.toString()}`);
  }

  async getExtension(id: string) {
    return this.request(`/extensions/${id}`);
  }

  async createExtension(data: {
    store_id?: string;
    extension_number: string;
    password: string;
    caller_id_name?: string;
    caller_id_number?: string;
    enabled?: boolean;
  }) {
    return this.request('/extensions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExtension(id: string, data: Partial<{
    store_id?: string;
    extension_number: string;
    password: string;
    caller_id_name?: string;
    caller_id_number?: string;
    enabled?: boolean;
  }>) {
    return this.request(`/extensions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteExtension(id: string) {
    return this.request(`/extensions/${id}`, {
      method: 'DELETE',
    });
  }

  // Users
  async getUsers() {
    return this.request('/users');
  }

  async createUser(data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: 'admin' | 'agent' | 'user';
  }) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: Partial<{
    first_name: string;
    last_name: string;
    role: 'admin' | 'agent' | 'user';
    status: 'active' | 'inactive' | 'suspended';
  }>) {
    return this.request(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // CDR
  async getCDR(params?: {
    page?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    call_direction?: string;
    caller_number?: string;
    callee_number?: string;
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) query.append(key, value.toString());
    });
    
    return this.request(`/cdr?${query.toString()}`);
  }

  async getCDRStats(params?: {
    start_date?: string;
    end_date?: string;
    call_direction?: string;
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) query.append(key, value.toString());
    });
    
    return this.request(`/cdr/stats?${query.toString()}`);
  }

  async exportCDR(format: 'csv' | 'json', params?: Record<string, string | number | boolean | null | undefined>) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) query.append(key, value.toString());
    });
    
    return this.request(`/cdr/export/${format}?${query.toString()}`);
  }

  // Calls
  async originateCall(data: {
    caller_extension: string;
    callee_number: string;
    domain: string;
    options?: {
      timeout?: number;
      caller_id?: string;
      context?: string;
      recording?: boolean;
    };
  }) {
    return this.request('/calls/originate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async transferCall(data: {
    call_uuid: string;
    destination: string;
    type?: 'attended' | 'blind';
  }) {
    return this.request('/calls/transfer', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async hangupCall(data: {
    call_uuid: string;
    cause?: string;
  }) {
    return this.request('/calls/hangup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async holdCall(data: {
    call_uuid: string;
    hold?: boolean;
  }) {
    return this.request('/calls/hold', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Task #4702 — mute/record/info/status rimossi: le rotte backend erano
  // morte (metodi assenti su ESLService → 500) e senza chiamanti. Per la
  // registrazione usare `/voip/calls/recording`.

  // ===== MASTER TENANT & CROSS-TENANT MANAGEMENT =====

  // Cross-tenant statistics
  // Task #3266 — filtro opzionale per prodotto/edition (`tenants.product`).
  async getCrossTenantStats(product?: string) {
    return this.request(`/master/dashboard/stats${product ? `?product=${encodeURIComponent(product)}` : ''}`);
  }

  async getTenantStatsList(product?: string) {
    return this.request(`/master/dashboard/tenants${product ? `?product=${encodeURIComponent(product)}` : ''}`);
  }

  // Tenant management with companies and admin user (Task #2454: i referenti
  // non sono più provisionati in creazione tenant; `companies` = sole ragioni
  // sociali del tenant).
  async createTenantWithCompanies(data: {
    name: string;
    slug: string;
    domain: string;
    sip_domain: string;
    admin_user: {
      first_name: string;
      last_name: string;
      email: string;
      password: string;
      role: 'admin' | 'super_admin';
    };
    companies: Array<{
      legal_name: string;
      vat_number?: string;
      tax_code?: string;
      address?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    }>;
    modules?: {
      pos_warehouse?: boolean;
    };
    settings?: {
      max_extensions?: number;
      max_trunks?: number;
      max_concurrent_calls?: number;
      max_users?: number;
      max_ai_agents?: number;
      recording_enabled?: boolean;
      gdpr_compliant?: boolean;
      voicemail_directory?: string;
    };
  }) {
    return this.request('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTenantWithDetails(id: string) {
    return this.request(`/tenants/${id}`);
  }

  async updateTenantWithDetails(id: string, data: Record<string, unknown>) {
    return this.request(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async activateTenant(id: string) {
    return this.request(`/tenants/${id}/activate`, {
      method: 'POST',
    });
  }

  async suspendTenant(id: string) {
    return this.request(`/tenants/${id}/suspend`, {
      method: 'POST',
    });
  }

  // User impersonation — reason opzionale ma raccomandato (audit log Task #1788)
  async impersonateUser(tenantId: string, userId: string, reason?: string) {
    return this.request(`/master/dashboard/impersonate/${userId}`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    });
  }

  // Cross-tenant analytics
  async getCrossTenantCalls(params?: {
    start_date?: string;
    end_date?: string;
    tenant_id?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });
    
    return this.request(`/master/analytics/cross-tenant/calls?${query.toString()}`);
  }

  async getCrossTenantExtensions() {
    return this.request('/master/analytics/cross-tenant/extensions');
  }

  async getCrossTenantLiveCalls() {
    return this.request('/master/analytics/cross-tenant/live-calls');
  }

  async getCrossTenantUsers(): Promise<ApiResponse<CrossTenantUserListResponse>> {
    return this.get<CrossTenantUserListResponse>('/master/users');
  }

  async getCrossTenantTenantOptions(): Promise<ApiResponse<CrossTenantTenantOption[]>> {
    return this.get<CrossTenantTenantOption[]>('/master/users/tenants');
  }

  async getCrossTenantCompanies() {
    return this.request('/master/analytics/cross-tenant/companies');
  }

  async getCrossTenantSummary(params?: {
    period?: '1h' | '24h' | '7d' | '30d';
  }) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });
    
    return this.request(`/master/analytics/cross-tenant/summary?${query.toString()}`);
  }

  private originalToken: string | null = null;

  startImpersonation(impersonationToken: string) {
    this.originalToken = this.token;
    this.token = impersonationToken;
    localStorage.setItem(AUTH_STORAGE_KEYS.BACKUP_TOKEN, this.originalToken || '');
    localStorage.setItem(AUTH_STORAGE_KEYS.IMPERSONATION_ACTIVE, 'true');
  }

  exitImpersonation() {
    const backupToken = localStorage.getItem(AUTH_STORAGE_KEYS.BACKUP_TOKEN);
    if (backupToken) {
      this.token = backupToken;
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, backupToken);
    }
    this.originalToken = null;
    localStorage.removeItem(AUTH_STORAGE_KEYS.BACKUP_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATION_ACTIVE);
    localStorage.removeItem(AUTH_STORAGE_KEYS.IMPERSONATED_TENANT);
  }

  isImpersonating(): boolean {
    return localStorage.getItem(AUTH_STORAGE_KEYS.IMPERSONATION_ACTIVE) === 'true';
  }

  // ========================================================================
  // Master Users (admin master schema) — Task #1762/#1763
  // Mount: top-level /api/master/master-users (NO tenant slug prefix).
  // ========================================================================
  async listMasterUsers(filters?: MasterUserListFilters): Promise<ApiResponse<MasterUserListResponse>> {
    const query = new URLSearchParams();
    if (filters?.search) query.append('search', filters.search);
    if (filters?.role) query.append('role', filters.role);
    if (filters?.status) query.append('status', filters.status);
    if (typeof filters?.totp_enabled === 'boolean') {
      query.append('totp_enabled', String(filters.totp_enabled));
    }
    if (typeof filters?.limit === 'number') query.append('limit', String(filters.limit));
    if (typeof filters?.offset === 'number') query.append('offset', String(filters.offset));
    const qs = query.toString();
    return this.get<MasterUserListResponse>(`/master/master-users${qs ? `?${qs}` : ''}`);
  }

  async getMasterUser(userId: string): Promise<ApiResponse<MasterUserDetail>> {
    return this.get<MasterUserDetail>(`/master/master-users/${userId}`);
  }

  async getMasterUserSessions(userId: string): Promise<ApiResponse<{ user_id: string; sessions: MasterRefreshTokenInfo[] }>> {
    return this.get(`/master/master-users/${userId}/sessions`);
  }

  async createMasterUser(body: MasterUserCreateRequest): Promise<ApiResponse<{ user: MasterUser; invite: { email_sent: boolean; expires_at: string } }>> {
    return this.post(`/master/master-users`, body);
  }

  async updateMasterUser(userId: string, body: MasterUserUpdateRequest): Promise<ApiResponse<MasterUser>> {
    return this.patch<MasterUser>(`/master/master-users/${userId}`, body);
  }

  async resetMasterUserPassword(userId: string, body: MasterUserResetPasswordRequest): Promise<ApiResponse<{ user_id: string; reset_email_sent: boolean; expires_at: string }>> {
    return this.post(`/master/master-users/${userId}/reset-password`, body);
  }

  async disableMasterUserTwoFactor(userId: string, body: MasterUserDisableTwoFactorRequest): Promise<ApiResponse<{ user_id: string; totp_enabled: false }>> {
    return this.post(`/master/master-users/${userId}/disable-2fa`, body);
  }

  async revokeMasterUserSessions(userId: string, body: MasterUserRevokeSessionsRequest): Promise<ApiResponse<{ user_id: string; revoked_count: number }>> {
    return this.post(`/master/master-users/${userId}/revoke-sessions`, body);
  }

  async deleteMasterUser(userId: string, body: MasterUserDeleteRequest): Promise<ApiResponse<{ user_id: string; deleted: true }>> {
    return this.request(`/master/master-users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  }

  // ========================================================================
  // Master Roles
  // ========================================================================
  async listMasterRoles(): Promise<ApiResponse<Array<MasterRole & { user_count: number }>>> {
    return this.get(`/master/master-roles`);
  }

  async getMasterRole(roleId: string): Promise<ApiResponse<MasterRoleDetail>> {
    return this.get<MasterRoleDetail>(`/master/master-roles/${roleId}`);
  }

  async createMasterRole(body: MasterRoleCreateRequest): Promise<ApiResponse<MasterRole>> {
    return this.post<MasterRole>(`/master/master-roles`, body);
  }

  async updateMasterRole(roleId: string, body: MasterRoleUpdateRequest): Promise<ApiResponse<MasterRole>> {
    return this.patch<MasterRole>(`/master/master-roles/${roleId}`, body);
  }

  async deleteMasterRole(roleId: string): Promise<ApiResponse<{ role_id: string; deleted: true }>> {
    return this.delete(`/master/master-roles/${roleId}`);
  }

  // ========================================================================
  // Master Permissions
  // ========================================================================
  async listMasterPermissionCatalog(params?: { category?: string; search?: string }): Promise<ApiResponse<MasterPermissionCatalog>> {
    const q = new URLSearchParams();
    if (params?.category) q.append('category', params.category);
    if (params?.search) q.append('search', params.search);
    const qs = q.toString();
    return this.get<MasterPermissionCatalog>(`/master/master-permissions/catalog${qs ? `?${qs}` : ''}`);
  }

  async getMasterRolePermissions(roleId: string): Promise<ApiResponse<{ role_id: string; role_name: string; permissions: import('@easyflow/shared').MasterPermission[]; permission_ids: string[] }>> {
    return this.get(`/master/master-permissions/role/${roleId}`);
  }

  async assignMasterRolePermissions(roleId: string, body: MasterRolePermissionAssignmentRequest): Promise<ApiResponse<MasterRolePermissionAssignmentResponse>> {
    return this.put<MasterRolePermissionAssignmentResponse>(`/master/master-permissions/role/${roleId}`, body);
  }

  // Security & System Monitoring
  async getSystemMetrics() {
    return this.request('/security/system/metrics');
  }

  async getNetworkMetrics() {
    return this.request('/security/network/metrics');
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;